// Tap-to-select quality tags shown to a client rating a completed commission.
// Each tag maps 1:1 to a catalog.ts skill badge — this is the missing signal
// that flips those badges from `computable: false` to `computable: true`
// (see compute.ts gatherUnifiedStats + catalog.ts comments at each slug).
//
// `section` groups tags in the picker UI. `appliesTo` filters which sections
// show for a given commission (a script commission never shows art tags).
// Universal/community tags always show regardless of commission type.

export type ReviewTagSection =
  | "overall"
  | "writing"
  | "art"
  | "sonic"
  | "voice"
  | "edit"
  | "community"
  | "goonfolio"
  | "goonpal"
  | "gameplay"
  | "interaction"
  | "game-story"
  | "game-polish"
  | "game-community"
  | "needs-work"

export type ReviewTargetType = "scriptor" | "artist" | "goonfolio" | "goonpal" | "game"
export type ReviewTagSentiment = "positive" | "negative"

export interface ReviewTag {
  slug: string
  label: string
  section: ReviewTagSection
  sentiment?: ReviewTagSentiment
  /** Which rating target type(s) this tag is offered for. Omit for universal/community tags. */
  appliesTo?: ReviewTargetType[]
  /** catalog.ts badge slug(s) this tag counts toward. */
  badgeSlugs: string[]
}

export const REVIEW_TAGS: ReviewTag[] = [
  // ── Overall / universal — shown on every commission type ────────────────
  { slug: "creative-vision", label: "Creative, original take", section: "overall", badgeSlugs: ["visionary"] },
  { slug: "fresh-ideas", label: "Tried something new", section: "overall", badgeSlugs: ["innovator"] },
  { slug: "mixed-media", label: "Great mix of media", section: "overall", badgeSlugs: ["mediaweaver"] },

  // ── Writing (scriptor) ────────────────────────────────────────────────
  { slug: "believable-characters", label: "Believable characters & dialogue", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["realist"] },
  { slug: "great-pacing", label: "Great scene pacing", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["director"] },
  { slug: "great-buildup", label: "Great buildup & anticipation", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["preluder"] },
  { slug: "teasing-restraint", label: "Loved the teasing/restraint", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["edgelord"] },
  { slug: "great-rhythm", label: "Great timing & rhythm", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["rhythmic"] },
  { slug: "technical-scripting", label: "Impressive technical scripting", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["engineer"] },
  { slug: "psychological-depth", label: "Emotionally/psychologically deep", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["psychologist"] },
  { slug: "smooth-flow", label: "Smooth scene-to-scene flow", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["flowmaster"] },
  { slug: "oral-scene-quality", label: "Great oral-focused writing", section: "writing", appliesTo: ["scriptor"], badgeSlugs: ["bjmaster"] },

  // ── Art (2D/3D artists) ──────────────────────────────────────────────
  { slug: "strong-illustration", label: "Strong illustration", section: "art", appliesTo: ["artist"], badgeSlugs: ["illustrator"] },
  { slug: "great-color", label: "Great color & lighting", section: "art", appliesTo: ["artist"], badgeSlugs: ["colorist"] },
  { slug: "unique-style", label: "Distinctive, recognizable style", section: "art", appliesTo: ["artist"], badgeSlugs: ["stylizer"] },
  { slug: "great-composition", label: "Great composition & layout", section: "art", appliesTo: ["artist"], badgeSlugs: ["designer"] },
  { slug: "amazing-detail", label: "Incredible detail & precision", section: "art", appliesTo: ["artist"], badgeSlugs: ["detailsmith"] },
  { slug: "rich-worldbuilding", label: "Rich, immersive environment", section: "art", appliesTo: ["artist"], badgeSlugs: ["worldbuilder"] },
  { slug: "powerful-impact", label: "Powerful action/effects", section: "art", appliesTo: ["artist"], badgeSlugs: ["impact"] },
  { slug: "expressive-acting", label: "Expressive faces & body language", section: "art", appliesTo: ["artist"], badgeSlugs: ["expressive"] },
  { slug: "great-sculpting", label: "Great 3D modeling/sculpting", section: "art", appliesTo: ["artist"], badgeSlugs: ["sculptor"] },
  { slug: "great-lighting", label: "Excellent lighting & rendering", section: "art", appliesTo: ["artist"], badgeSlugs: ["lightingartist"] },
  { slug: "great-materials", label: "Great materials & textures", section: "art", appliesTo: ["artist"], badgeSlugs: ["materialmaster"] },
  { slug: "technical-artistry", label: "Impressive technical pipeline", section: "art", appliesTo: ["artist"], badgeSlugs: ["technicalartist"] },
  { slug: "natural-motion", label: "Natural secondary motion/physics", section: "art", appliesTo: ["artist"], badgeSlugs: ["motionmaster"] },

  // ── Sonic (music/sound design) ───────────────────────────────────────
  { slug: "original-music", label: "Outstanding original music", section: "sonic", appliesTo: ["artist"], badgeSlugs: ["composer"] },
  { slug: "great-mixing", label: "Great mixing/mastering", section: "sonic", appliesTo: ["artist"], badgeSlugs: ["mixmaster"] },

  // ── Voice (VA/ASMR) ──────────────────────────────────────────────────
  { slug: "great-voice-acting", label: "Exceptional voice performance", section: "voice", appliesTo: ["artist"], badgeSlugs: ["vocalvirtuoso"] },

  // ── Edit (video editors) ─────────────────────────────────────────────
  { slug: "clean-editing", label: "Clean, polished editing", section: "edit", appliesTo: ["artist"], badgeSlugs: ["editor"] },
  { slug: "cinematic-feel", label: "Cinematic presentation", section: "edit", appliesTo: ["artist"], badgeSlugs: ["cinematic"] },
  { slug: "great-colorgrade", label: "Great color grading", section: "edit", appliesTo: ["artist"], badgeSlugs: ["colorgrade"] },
  { slug: "great-sequencing", label: "Great scene sequencing", section: "edit", appliesTo: ["artist"], badgeSlugs: ["sequencer"] },

  // ── Community — always shown ─────────────────────────────────────────
  { slug: "crowd-favorite", label: "Would recommend to others", section: "community", badgeSlugs: ["communityfavorite"] },
  { slug: "helpful-supportive", label: "Helpful & supportive to work with", section: "community", badgeSlugs: ["supporter"] },

  // ── Goonfolio (rating a creator's portfolio, not a commission) ────────
  { slug: "well-organized", label: "Beautifully organized folio", section: "goonfolio", appliesTo: ["goonfolio"], badgeSlugs: ["curator"] },
  { slug: "realistic-characters", label: "Realistic, believable characters", section: "goonfolio", appliesTo: ["goonfolio"], badgeSlugs: ["realism"] },
  { slug: "great-original-characters", label: "Great original character design", section: "goonfolio", appliesTo: ["goonfolio"], badgeSlugs: ["charactersmith"] },
  { slug: "well-documented", label: "Well-documented, thorough collection", section: "goonfolio", appliesTo: ["goonfolio"], badgeSlugs: ["archivist"] },
  { slug: "great-presentation", label: "Great overall presentation", section: "goonfolio", appliesTo: ["goonfolio"], badgeSlugs: ["showcase"] },
  { slug: "accurate-recreation", label: "Accurate recreation of existing characters", section: "goonfolio", appliesTo: ["goonfolio"], badgeSlugs: ["canonkeeper"] },

  // ── GoonPal (rating a companion you've actually talked to) ─────────────
  { slug: "feels-lifelike", label: "Feels lifelike and immersive", section: "goonpal", appliesTo: ["goonpal"], badgeSlugs: ["immersion"] },
  { slug: "great-writing", label: "Great writing & dialogue", section: "goonpal", appliesTo: ["goonpal"], badgeSlugs: ["wordsmith"] },

  // ── Game Arena (rating a developer's game you've claimed/purchased) ───
  { slug: "strong-gameplay-loop", label: "Strong gameplay loop", section: "gameplay", appliesTo: ["game"], badgeSlugs: ["gamedesigner"] },
  { slug: "balanced-progression", label: "Balanced progression", section: "gameplay", appliesTo: ["game"], badgeSlugs: ["gamedesigner"] },
  { slug: "meaningful-choices", label: "Meaningful choices", section: "interaction", appliesTo: ["game"], badgeSlugs: ["interactionmaster"] },
  { slug: "responsive-mechanics", label: "Responsive mechanics", section: "interaction", appliesTo: ["game"], badgeSlugs: ["interactionmaster"] },
  { slug: "great-story-lore", label: "Great story/lore", section: "game-story", appliesTo: ["game"], badgeSlugs: ["storyweaver"] },
  { slug: "memorable-characters", label: "Memorable characters", section: "game-story", appliesTo: ["game"], badgeSlugs: ["storyweaver"] },
  { slug: "clean-presentation", label: "Clean presentation", section: "game-polish", appliesTo: ["game"], badgeSlugs: ["polish"] },
  { slug: "smooth-production", label: "Smooth production", section: "game-polish", appliesTo: ["game"], badgeSlugs: ["polish"] },
  { slug: "fun-replayable", label: "Fun/replayable", section: "game-community", appliesTo: ["game"], badgeSlugs: ["crowdpleaser"] },
  { slug: "would-recommend", label: "Would recommend", section: "game-community", appliesTo: ["game"], badgeSlugs: ["crowdpleaser"] },

  // ── Constructive/negative feedback — saved for creator analytics, but
  // deliberately maps to no badges. Bad reviews should help creators improve,
  // not directly subtract from badge progress.
  { slug: "buggy", label: "Buggy", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "confusing-controls", label: "Confusing controls", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "poor-performance", label: "Poor performance", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "repetitive", label: "Repetitive", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "weak-story", label: "Weak story", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "unclear-goal", label: "Unclear goal", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "rough-ui", label: "Rough UI", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "audio-issues", label: "Audio issues", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "not-enough-content", label: "Not enough content", section: "needs-work", sentiment: "negative", appliesTo: ["game"], badgeSlugs: [] },
  { slug: "missed-expectations", label: "Missed expectations", section: "needs-work", sentiment: "negative", badgeSlugs: [] },
  { slug: "communication-issues", label: "Communication issues", section: "needs-work", sentiment: "negative", appliesTo: ["scriptor", "artist"], badgeSlugs: [] },
  { slug: "late-delivery", label: "Late delivery", section: "needs-work", sentiment: "negative", appliesTo: ["scriptor", "artist"], badgeSlugs: [] },
  { slug: "needs-more-polish", label: "Needs more polish", section: "needs-work", sentiment: "negative", badgeSlugs: [] },
  { slug: "hard-to-follow", label: "Hard to follow", section: "needs-work", sentiment: "negative", appliesTo: ["scriptor", "goonpal", "goonfolio"], badgeSlugs: [] },
  { slug: "not-immersive", label: "Not immersive", section: "needs-work", sentiment: "negative", appliesTo: ["goonpal", "goonfolio"], badgeSlugs: [] },
]

export function reviewTagsFor(targetType: ReviewTargetType): ReviewTag[] {
  return REVIEW_TAGS.filter((t) => !t.appliesTo || t.appliesTo.includes(targetType))
}

export function getReviewTag(slug: string): ReviewTag | undefined {
  return REVIEW_TAGS.find((t) => t.slug === slug)
}

export function reviewTagSentiment(tag: ReviewTag): ReviewTagSentiment {
  return tag.sentiment ?? "positive"
}

export const REVIEW_TAG_SLUGS = new Set(REVIEW_TAGS.map((t) => t.slug))

/** Every badge slug backed by at least one review tag — includes badges still
 * `computable: false` in catalog.ts (held on missing art), so their progress
 * can still be shown even though they can't be earned yet. */
export const TAG_BACKED_BADGE_SLUGS = new Set(REVIEW_TAGS.flatMap((t) => t.badgeSlugs))

/** Sums how many completed-commission reviews mentioned each badge, given the
 * raw `tags` arrays from a creator's `commission_review_tags` rows. Used by
 * compute.ts to feed the badges review-tags now back (see catalog.ts). */
export function countBadgesFromReviewTags(tagRows: string[][]): Record<string, number> {
  const badgeCounts: Record<string, number> = {}
  for (const tags of tagRows) {
    // A single review can only count once per badge even if (hypothetically)
    // multiple tags mapped to the same badge — count distinct badges hit.
    const badgesInThisReview = new Set<string>()
    for (const tagSlug of tags) {
      const tag = getReviewTag(tagSlug)
      if (!tag) continue
      for (const badgeSlug of tag.badgeSlugs) badgesInThisReview.add(badgeSlug)
    }
    for (const badgeSlug of badgesInThisReview) {
      badgeCounts[badgeSlug] = (badgeCounts[badgeSlug] || 0) + 1
    }
  }
  return badgeCounts
}
